import {
  type DubLevels,
  DubLevelSlider,
  formatDubLevel,
} from '@src/components/aiVoices/DubLevelSlider';
import Checkbox from '@src/components/common/Checkbox';
import ProgressBar from '@src/components/common/ProgressBar';
import { MODAL_COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import { useAnchoredDropdown } from '@src/components/common/useAnchoredDropdown';
import { useEstimatedProgress } from '@src/components/common/useEstimatedProgress';
import { estimateStemSeparationMs } from '@src/config/processingEstimates';
import { leadInOriginalLevel, sameDubLeadIn } from '@src/editor/dubbing/leadIn';
import CheckIcon from '@src/svgs/CheckIcon';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled, { keyframes } from 'styled-components';

import { ReviewLeadInPanel } from './ReviewLeadInPanel';
import {
  PickerMenu,
  PickerRow,
  PickerRowMeta,
  PickerTrigger,
} from './reviewPaneHeaderPicker';

import type { AudioMode } from '@src/components/aiVoices/AudioModeSegmentedToggle';
import type { CustomKaraokeTemplateOption } from '@src/components/textProcessor/TextBrandingService';
import type { DubMusicMode as MusicMode } from '@src/editor/dubbing/DubEngine';
import type { DubLeadIn } from '@src/editor/dubbing/leadIn';
import type { CSSProperties, ReactNode } from 'react';

export const VOICE_OPTIONS: { id: AudioMode; label: string; hint: string }[] = [
  { id: 'original', label: 'Original', hint: 'The recording’s own voice' },
  { id: 'dubbed', label: 'Dubbed', hint: 'The AI voice alone' },
  { id: 'both', label: 'Both', hint: 'Original under the dub, for A-B' },
];

export const MUSIC_OPTIONS: { id: MusicMode; label: string; hint: string }[] = [
  { id: 'none', label: 'None', hint: 'The dub alone' },
  {
    id: 'original',
    label: 'From Original',
    hint: 'The story’s music under the dub',
  },
];

const TOOLTIP_ID = 'review-player-audio-tooltip';

/**
 * A caption template row is drawn in the template's own face — the face only:
 * its fill and stroke are chosen to read over video, and would be invisible
 * (or unreadable) on the menu's surface.
 */
const templateLabelStyle = (
  template: CustomKaraokeTemplateOption,
): CSSProperties => ({
  fontFamily: template.fontFamily,
  fontWeight: template.fontWeight,
  fontStyle: template.italic ? 'italic' : 'normal',
});

export type PickerOption<T extends string> = {
  id: T;
  label: string;
  hint: string;
  /**
   * PLAT-11702 follow-up — draw the label in this face. Used by the caption
   * template rows so a template is recognisable by its own type, not just its
   * name; brand faces are registered by postScript name, which the hosts load
   * (`useLoadBrandKitFonts`).
   */
  labelStyle?: CSSProperties;
  /**
   * PLAT-11877 — a heading drawn above this row when it opens a new group
   * (the rows before it carry a different `group`, or none): "Workspace
   * Templates" over the showcase's caption templates, so the menu says where
   * those styles come from.
   */
  group?: string;
  /**
   * PLAT-11877 — ticked regardless of the picker's `value`. The Captions menu
   * answers two questions (is it on; which style), so its style rows carry
   * their own tick beside the On / Off one.
   */
  selected?: boolean;
};

/** PLAT-11877 — the heading over the showcase's caption template rows. */
export const WORKSPACE_TEMPLATES_GROUP = 'Workspace Templates';
/** PLAT-11877 — the heading over a style of the video's own the list lacks. */
export const CURRENT_STYLE_GROUP = 'Current Style';

/** PLAT-11683 — captions over the review player, on or off. */
export type CaptionsMode = 'on' | 'off';

/**
 * PLAT-11702 follow-up — the Captions picker's own value. On a video with no
 * captions of its own the On row is replaced by one row per showcase caption
 * template, so the value carries WHICH template as well as on/off.
 */
type CaptionsPickerValue = CaptionsMode | 'own' | `template:${string}`;

const TEMPLATE_VALUE_PREFIX = 'template:';

/**
 * PLAT-11702 follow-up — the showcase's caption templates the picker offers
 * for a video that has none of its own, the one currently drawn, and the pick
 * handler. Absent / empty ⇒ the picker stays On / Off.
 */
export interface ReviewCaptionTemplateChoice {
  options: CustomKaraokeTemplateOption[];
  /**
   * `null` ⇒ the video's own style when it has one (`ownStyleLabel`), else
   * the head of `options` (the showcase's most recently used).
   */
  selectedId: string | null;
  /** `null` ⇒ back to the video's own style. */
  onSelect: (templateId: string | null) => void;
  /**
   * The video's own caption style, listed first and selected by default.
   * Absent ⇒ the video has none and the rows are the templates alone.
   */
  ownStyle?: ReviewOwnCaptionStyle | null;
}

/**
 * The style the video already carries. Named after the template it came from
 * when the showcase still has that one — the row then stands in for it, and
 * the template is not listed twice. `label` covers the rest: a preset, a
 * template since deleted, or one saved at another aspect ratio.
 */
export interface ReviewOwnCaptionStyle {
  templateId?: string | null;
  label: string;
  /**
   * PLAT-11877 — where the style came from, as the row's meta: "Workspace
   * Template", "Arbor Preset" or "Custom Style". The row is otherwise just a
   * name, and a name alone does not say whether the showcase set it up.
   */
  source?: string;
}

/** PLAT-11702 — hover on the inert picker when there is nothing to show. */
export const CAPTIONS_UNAVAILABLE_REASON =
  'This showcase has no caption template. Ask an admin to add one in Brand Kit.';

/**
 * PLAT-11714 — the story's voice/music separation, so the review can MAKE the
 * bed its Music picker needs instead of only reporting that there isn't one.
 * Until now the only way in was the Transcript & Speakers language menu, which
 * is two screens away from the person listening to the dub.
 *
 * A narrow view of `useMusicSeparationController`, which the review's host
 * already owns (the toggle and live playback share one `musicMode` / `bedUrl`,
 * DS6) — the review never runs a controller of its own. Omitted ⇒ the picker
 * behaves as before: inert until a bed exists, made elsewhere. The clip entry
 * omits it deliberately; separation is a story-wide, minutes-long job that does
 * not belong inside one clip's screen (Denys, 2026-08-19).
 *
 * Re-separate, the stem downloads and the splitter picker stay in the language
 * menu. They act on the story rather than on what is playing, and the review is
 * a monitoring surface.
 */
export interface ReviewMusicSeparation {
  /** A run is in flight — the picker shows its estimate instead of an action. */
  separating: boolean;
  /**
   * When that run started (epoch ms), from the controller rather than this
   * component, so closing and reopening the menu doesn't rewind the estimate
   * to 0% (the job outlives the panel — PLAT-11563).
   */
  startedAt: number | null;
  /** Source length, for the progress estimate; `null` ⇒ the generic fallback. */
  durationSec: number | null;
  /** Separated already, but the kept bed is silent: there is no music to lay. */
  noMusic: boolean;
  /**
   * A non-deleted stems variant exists. Set the moment separation resolves,
   * while the signed bed URL is still being read back — so the picker can tell
   * "separated, URL landing" from "never separated" and not fall back to
   * offering Separate over a bed that already exists (PLAT-11714).
   */
  hasStems: boolean;
  /**
   * Set ⇒ Separate can't run (over the length cap, or the state read is still
   * in flight). The picker stays inert with this on hover rather than opening
   * onto a dead button.
   */
  blockedReason?: string | null;
  onSeparate: () => void;
  /**
   * Stop waiting on the run in flight. The server may still finish it, in which
   * case the bed turns up on the next read.
   */
  onCancel: () => void;
}

/** Hover on the Music picker when this entry offers no way to make a bed. */
export const MUSIC_NO_BED_REASON =
  'Separate the story’s music first to hear it under the dub';

/**
 * Hover while the stems exist but their signed URL has not landed yet. The
 * controller sets `hasStems` in the same tick separation resolves and fetches
 * the URL in a second request, so this window is a real one (and a failed
 * fetch leaves it open) — the picker waits here rather than re-offering a
 * separation that has already run.
 */
const MUSIC_BED_PENDING_REASON = 'Preparing the separated music…';

export interface ReviewPlayerAudioPickersProps {
  /**
   * PLAT-11683 — the Captions picker, first in the row (left of Voice). A
   * viewing choice like the rest: the review never writes captions
   * (PLAT-11702).
   */
  captionsMode: CaptionsMode;
  onCaptionsModeChange: (mode: CaptionsMode) => void;
  /** The On row's hint — differs by entry (preview vs. real captions). */
  captionsOnHint: string;
  /** When set the Captions picker is inert with this reason on hover. */
  captionsInertReason?: string | null;
  /**
   * PLAT-11702 — the video has no captions and the showcase has no caption
   * template, so there is nothing of the showcase's to show. The picker reads
   * "No captions set up" and is inert, with the way out on hover.
   */
  captionsUnavailable?: boolean;
  /**
   * When set, a "Preview" tag sits beside the Captions picker with this
   * reason on hover: the captions on screen are the review's own render, not
   * ones saved to the story or clip.
   */
  captionsPreviewReason?: string | null;
  /**
   * PLAT-11702 follow-up — when the video has no captions of its own, the
   * showcase's caption templates to choose between (the review draws the pick;
   * it is never saved). Omitted on a video that HAS captions — there the
   * picker stays On / Off and On means the video's own style.
   */
  captionTemplateChoice?: ReviewCaptionTemplateChoice;
  /**
   * Voice and Music: Dubbing Review with a dubbed language only. Off in
   * Translation Review, where the row carries Captions alone.
   */
  showAudio: boolean;
  audioMode: AudioMode;
  onAudioModeChange: (mode: AudioMode) => void;
  musicMode: MusicMode;
  onMusicModeChange: (mode: MusicMode) => void;
  /** The story has a kept music bed to lay under the dub. */
  musicAvailable: boolean;
  /**
   * PLAT-11899 / PLAT-11927 — the dub's levels in this review: the dubbed
   * voice, and the original under it on Both. Transient like Voice and Music:
   * moving them changes what the review plays and nothing on the clip.
   */
  levels: DubLevels;
  onLevelsChange: (levels: DubLevels) => void;
  /**
   * Save the review's levels onto the clip — the ones the picker shows; a
   * level left out stays where the clip has it. Clip entry only: the
   * whole-story entry has no clip to save to, and without this the picker
   * says its levels are a preview.
   */
  onApplyLevels?: (
    levels: Partial<DubLevels>,
    /** Passed when the panel offers a lead-in; `null` turns it off. */
    leadIn?: DubLeadIn | null,
  ) => void;
  /** The levels the clip is saved with, so Apply can say when there is nothing to apply. */
  appliedLevels?: DubLevels;
  /**
   * The original lead-in on Both, heard in this review like the levels and
   * kept by the same Apply to Clip; `undefined` is off.
   */
  leadIn?: DubLeadIn;
  /** The lead-in the clip is saved with. */
  appliedLeadIn?: DubLeadIn;
  /** Hear another lead-in. Clip entry only: omitted ⇒ the panel offers none. */
  onLeadInChange?: (next: DubLeadIn | undefined) => void;
  /**
   * The dubbed line under the playhead, for setting its levels alone. Clip
   * entry only, and only while a dubbed line is on the clock; omitted ⇒ the
   * panel offers no "this line only" choice.
   */
  levelsLine?: ReviewLevelsLine;
  /**
   * PLAT-11714 — when there is no bed yet, the way to make one from here.
   * Omitted ⇒ the Music picker is inert until one is made elsewhere.
   */
  musicSeparation?: ReviewMusicSeparation;
  /** Sits above the workspace overlay (which varies by entry). */
  menuZIndex: number;
}

/**
 * One dubbed line's levels in the Levels panel, as the host keeps them: what
 * the review plays for the line, what the clip is saved with for it (the
 * clip's own levels where the line has none), and how to change and save them.
 */
export interface ReviewLevelsLine {
  segmentId: string;
  levels: DubLevels;
  onChange: (levels: DubLevels) => void;
  onApply: (levels: DubLevels) => void;
  /** Apply to Line would change what the clip has saved for this line. */
  unapplied: boolean;
  /** Put every line back on the levels the clip is saved with. */
  revert: () => void;
  /**
   * Keep what the line is heard at as its own levels, measured against the
   * clip's saved levels — for the clip's unapplied change to become the
   * line's alone. Off Both, the Dub only.
   */
  takeClipLevels: () => void;
  /**
   * Hold the panel on this line while "this line only" is ticked, so a
   * playhead moving on does not carry the sliders to the next one.
   */
  pin: (pinned: boolean) => void;
  /**
   * The line plays at levels of its own rather than the clip's, so what is
   * heard on it is `levels`.
   */
  hasOwnLevels: boolean;
  /**
   * The clip is saved with levels of its own for the line, so the panel opens
   * on the line. Levels only heard in this review do not: they are let go
   * when the panel closes without Apply.
   */
  hasSavedLevels: boolean;
}

/**
 * PLAT-11665 — Dubbing Review's player settings, in the review pane header's
 * picker style (the Paragraph / Segment / Words control): Voice — Original /
 * Dubbed / Both — and Music — None / From Original. Music applies under the
 * dubbed voice (and on a clip under Both too, PLAT-12244), and only when the
 * story has a bed, so the picker is inert with the reason on hover otherwise. Both are the review's transient monitoring
 * choices; nothing here is saved on the clip.
 *
 * PLAT-11683 — Captions leads the row in BOTH review modes, and is transient
 * in the same way: PLAT-11702 removed the last write path, so nothing this
 * picker does reaches the clip or the story. On a video with captions of its
 * own it is On / Off. On one without, the On row is replaced by the showcase's
 * caption templates, each drawn in its own face — picking one changes what the
 * review renders and nothing else (PLAT-11702 follow-up).
 */
export const ReviewPlayerAudioPickers: React.FC<
  ReviewPlayerAudioPickersProps
> = ({
  captionsMode,
  onCaptionsModeChange,
  captionsOnHint,
  captionsInertReason = null,
  captionsUnavailable = false,
  captionsPreviewReason = null,
  captionTemplateChoice,
  showAudio,
  audioMode,
  onAudioModeChange,
  musicMode,
  onMusicModeChange,
  musicAvailable,
  musicSeparation,
  levels,
  onLevelsChange,
  onApplyLevels,
  appliedLevels,
  levelsLine,
  leadIn,
  appliedLeadIn,
  onLeadInChange,
  menuZIndex,
}) => {
  const { captionsValue, captionsOptions, triggerLabel, pickFor } =
    buildCaptionsPickerOptions({
      captionsMode,
      captionsOnHint,
      captionsUnavailable,
      captionTemplateChoice,
    });
  // PLAT-11927 — the original's level is heard, and so offered, on Both only.
  const showOriginalLevel = audioMode === 'both';
  // What is heard right now: the line's own levels on a line that has them.
  const heardLevels =
    onApplyLevels && levelsLine?.hasOwnLevels ? levelsLine.levels : levels;
  // PLAT-11899 — the review is playing a level the clip is not saved at.
  // Only a review that can save has a saved level to differ from.
  // The lead-in is offered where the original's level is, on a clip.
  const leadInControl =
    showOriginalLevel && onApplyLevels && onLeadInChange
      ? { value: leadIn, applied: appliedLeadIn, onChange: onLeadInChange }
      : undefined;
  const { musicInert, separationPanel } = resolveMusicPicker({
    musicAvailable,
    musicSeparation,
    audioMode,
    // Only a clip's player builds the bed under Both; the story entry plays
    // the bed on its own, under Dubbed only. Only a clip is offered a lead-in.
    musicOnBoth: !!onLeadInChange,
  });
  const levelUnapplied =
    (!!onApplyLevels &&
      !!appliedLevels &&
      levelsDiffer(levels, appliedLevels, showOriginalLevel)) ||
    !!levelsLine?.unapplied ||
    (!!leadInControl && !sameDubLeadIn(leadIn, appliedLeadIn));
  const handleCaptionsPick = (next: CaptionsPickerValue) => {
    const pick = pickFor(next);
    if (pick.kind === 'template') {
      // The video's own template is its saved style, not a re-render of the
      // template; any other is a preview pick.
      captionTemplateChoice?.onSelect(
        pick.templateId === captionTemplateChoice.ownStyle?.templateId
          ? null
          : pick.templateId,
      );
    } else if (pick.kind === 'own') {
      // Back to the video's own captions, dropping any template preview.
      captionTemplateChoice?.onSelect(null);
    }
    if (pick.kind === 'off') onCaptionsModeChange('off');
    else if (captionsMode !== 'on') onCaptionsModeChange('on');
  };
  const captionsInert = captionsUnavailable
    ? CAPTIONS_UNAVAILABLE_REASON
    : captionsInertReason;
  return (
    <Pickers>
      {captionsPreviewReason && !captionsUnavailable && (
        <PreviewTag
          data-testid="review-captions-preview"
          data-tooltip-id={TOOLTIP_ID}
          data-tooltip-content={captionsPreviewReason}
        >
          Preview
        </PreviewTag>
      )}
      <HeaderPicker
        label="Captions"
        ariaLabel="Review captions"
        testId="review-captions-mode"
        value={captionsValue}
        options={captionsOptions}
        onChange={handleCaptionsPick}
        menuZIndex={menuZIndex}
        triggerValue={triggerLabel}
        inertReason={captionsInert}
      />
      {showAudio && (
        <>
          <HeaderPicker
            label="Voice"
            ariaLabel="Review voice"
            testId="review-voice-mode"
            value={audioMode}
            options={VOICE_OPTIONS}
            onChange={onAudioModeChange}
            menuZIndex={menuZIndex}
          />
          {
            // PLAT-11899 — only where a dub is heard: on Original there is
            // no dubbed voice to turn up or down. PLAT-11927 — on Both it
            // carries the original's level too, as one Levels picker.
          }
          {audioMode !== 'original' && (
            <HeaderPicker
              label={showOriginalLevel ? 'Mix' : 'Dub Level'}
              ariaLabel={showOriginalLevel ? 'Review mix' : 'Review dub level'}
              testId="review-dub-level"
              value="level"
              options={[
                {
                  id: 'level',
                  label: showOriginalLevel
                    ? `${heardLevels.originalVolume} / ${heardLevels.voiceVolume}`
                    : formatDubLevel(heardLevels.voiceVolume),
                  hint: '',
                },
              ]}
              onChange={() => undefined}
              menuZIndex={menuZIndex}
              menuRole="dialog"
              triggerMark={
                levelUnapplied ? (
                  <UnappliedDot
                    role="img"
                    aria-label="Not applied to the clip"
                    data-testid="review-dub-level-unapplied"
                  />
                ) : undefined
              }
              menuContent={close => (
                <DubLevelPanel
                  levels={levels}
                  showOriginal={showOriginalLevel}
                  onChange={onLevelsChange}
                  onApply={onApplyLevels}
                  appliedLevels={appliedLevels}
                  line={onApplyLevels ? levelsLine : undefined}
                  leadIn={leadInControl}
                  close={close}
                />
              )}
            />
          )}
          <HeaderPicker
            label="Music"
            ariaLabel="Review music"
            testId="review-music-mode"
            value={musicMode}
            options={MUSIC_OPTIONS}
            onChange={onMusicModeChange}
            menuZIndex={menuZIndex}
            inertReason={musicInert}
            menuContent={
              separationPanel ? (
                <MusicSeparationPanel separation={separationPanel} />
              ) : undefined
            }
            triggerValue={
              musicSeparation?.separating ? 'Separating…' : undefined
            }
          />
        </>
      )}
      {
        // Portaled: the right column clips its overflow and animates its
        // width, so a tooltip rendered inside it could be cut or mis-placed.
      }
      {createPortal(
        <Tooltip
          id={TOOLTIP_ID}
          style={MODAL_COMPACT_TOOLTIP_STYLE}
          place="top"
          positionStrategy="fixed"
        />,
        document.body,
      )}
    </Pickers>
  );
};

/** What a Captions row stands for, once picked. */
export type CaptionsPick =
  | { kind: 'on' }
  | { kind: 'own' }
  | { kind: 'off' }
  | { kind: 'template'; templateId: string };

/**
 * The Captions picker's rows, shared by the review player and the editor
 * player (PLAT-11877), so the menu reads the same wherever it opens. One
 * tick, always — a single-select list:
 *
 *  - **on**: Off first (never below the fold of a long list), then the
 *    styles — the showcase's caption templates under a "Workspace Templates"
 *    heading, each drawn in its own face, the one on the video ticked; a
 *    style of the video's the list lacks under "Current Style" with where it
 *    came from as its meta;
 *  - **off**: On, whose meta names the style it brings back or that a first
 *    "on" will apply ("Toro Red · Workspace Template"), and Off, ticked.
 *
 * On, Off, the headings and the meta stay in the UI face. Without
 * templates it is On / Off. `captionsUnavailable` collapses it to one inert
 * "No captions set up" row.
 */
export function buildCaptionsPickerOptions({
  captionsMode,
  captionsOnHint,
  captionsUnavailable = false,
  captionTemplateChoice,
}: {
  captionsMode: CaptionsMode;
  captionsOnHint: string;
  captionsUnavailable?: boolean;
  captionTemplateChoice?: ReviewCaptionTemplateChoice;
}): {
  captionsValue: CaptionsPickerValue;
  captionsOptions: PickerOption<CaptionsPickerValue>[];
  /** What the closed trigger says: On / Off, whichever style is on. */
  triggerLabel: string;
  pickFor: (next: CaptionsPickerValue) => CaptionsPick;
} {
  const templateOptions = captionTemplateChoice?.options ?? [];
  const showTemplates = !captionsUnavailable && templateOptions.length > 0;
  const ownStyle = captionTemplateChoice?.ownStyle ?? null;
  // The template the video's style came from, when the showcase still lists
  // it: that row then stands for the video's style (kept, not re-produced),
  // and no second row is added for it. A style the list lacks — a preset, a
  // hand-set look, a template from another aspect ratio — gets a row of its
  // own under "Current Style".
  const ownTemplate = ownStyle?.templateId
    ? templateOptions.find(
        template => template.templateId === ownStyle.templateId,
      )
    : undefined;
  const ownIsUnlisted = !!ownStyle && !ownTemplate;
  // Which style is on (or would come back / be applied): the host's pick,
  // else the video's own, else the default — the head of the list.
  const pickedId = captionTemplateChoice?.selectedId ?? null;
  const active: { name: string; source: string; key: CaptionsPickerValue } =
    pickedId && templateOptions.some(t => t.templateId === pickedId)
      ? {
          name: templateOptions.find(t => t.templateId === pickedId)!.title,
          source: 'Workspace Template',
          key: `${TEMPLATE_VALUE_PREFIX}${pickedId}`,
        }
      : ownTemplate
        ? {
            name: ownTemplate.title,
            source: ownStyle?.source ?? 'Workspace Template',
            key: `${TEMPLATE_VALUE_PREFIX}${ownTemplate.templateId}`,
          }
        : ownStyle
          ? { name: ownStyle.label, source: ownStyle.source ?? '', key: 'own' }
          : templateOptions[0]
            ? {
                name: templateOptions[0].title,
                source: 'Workspace Template',
                key: `${TEMPLATE_VALUE_PREFIX}${templateOptions[0].templateId}`,
              }
            : { name: '', source: '', key: 'on' };
  const captionsValue: CaptionsPickerValue = captionsMode;
  const activeHint = [active.name, active.source].filter(Boolean).join(' · ');
  const captionsOptions: PickerOption<CaptionsPickerValue>[] =
    captionsUnavailable
      ? [{ id: captionsValue, label: 'No captions set up', hint: '' }]
      : showTemplates
        ? captionsMode === 'on'
          ? [
              // Off first, so the way out is never below the fold of a long
              // template list — the on / off choice leads the menu in both
              // states. One tick: the style that is on IS the "on". A style
              // of the video's the list lacks leads under its own heading.
              { id: 'off', label: 'Off', hint: 'No captions', selected: false },
              ...(ownIsUnlisted && ownStyle
                ? [
                    {
                      id: 'own' as CaptionsPickerValue,
                      label: ownStyle.label,
                      hint: ownStyle.source ?? '',
                      group: CURRENT_STYLE_GROUP,
                      selected: active.key === 'own',
                    },
                  ]
                : []),
              ...templateOptions.map(
                (template): PickerOption<CaptionsPickerValue> => ({
                  id: `${TEMPLATE_VALUE_PREFIX}${template.templateId}`,
                  label: template.title,
                  hint: '',
                  group: WORKSPACE_TEMPLATES_GROUP,
                  labelStyle: templateLabelStyle(template),
                  selected:
                    active.key ===
                    `${TEMPLATE_VALUE_PREFIX}${template.templateId}`,
                }),
              ),
            ]
          : [
              // Off: the way back on, naming the style it brings back or
              // applies, and Off itself.
              { id: 'on', label: 'On', hint: activeHint || captionsOnHint },
              { id: 'off', label: 'Off', hint: 'No captions' },
            ]
        : [
            { id: 'on', label: 'On', hint: captionsOnHint },
            { id: 'off', label: 'Off', hint: 'No captions' },
          ];
  const pickFor = (next: CaptionsPickerValue): CaptionsPick => {
    if (next === 'off') return { kind: 'off' };
    if (next === 'on') return { kind: 'on' };
    if (next === 'own') return { kind: 'own' };
    return {
      kind: 'template',
      templateId: next.slice(TEMPLATE_VALUE_PREFIX.length),
    };
  };
  const triggerLabel = captionsUnavailable
    ? 'No captions set up'
    : captionsMode === 'on'
      ? 'On'
      : 'Off';
  return { captionsValue, captionsOptions, triggerLabel, pickFor };
}

// PLAT-11714 — with no bed the two positions mean nothing, so the menu
// carries the way to MAKE one instead (when this entry offers separation);
// the voice axis doesn't gate that, since separating is about the story's
// audio, not about what is currently playing.
// PLAT-11714 — the bed exists but its URL has not arrived: `musicAvailable`
// is still false, yet offering Separate here would re-run a job that just
// finished (and start a second one if clicked).
// On Both the bed makes up what the original is turned down by, where the
// player builds the clip's sound (`musicOnBoth`).
export function resolveMusicPicker({
  musicAvailable,
  audioMode,
  musicSeparation,
  musicOnBoth = false,
}: {
  musicAvailable: boolean;
  audioMode: AudioMode;
  musicSeparation?: ReviewMusicSeparation;
  musicOnBoth?: boolean;
}): {
  separationPanel: ReviewMusicSeparation | undefined;
  musicInert: string | null;
} {
  const bedPending =
    !musicAvailable &&
    !!musicSeparation &&
    musicSeparation.hasStems &&
    !musicSeparation.noMusic;
  // The run in flight still owns the menu — only the OFFER is withheld while
  // the URL lands, never the progress the reviewer is watching.
  const separationPanel =
    !musicAvailable &&
    musicSeparation &&
    (musicSeparation.separating || !bedPending)
      ? musicSeparation
      : undefined;
  const musicInert = musicAvailable
    ? audioMode === 'dubbed' || (audioMode === 'both' && musicOnBoth)
      ? null
      : 'Music plays under the dubbed voice — choose Dubbed first'
    : !musicSeparation
      ? MUSIC_NO_BED_REASON
      : musicSeparation.separating
        ? null
        : bedPending
          ? MUSIC_BED_PENDING_REASON
          : musicSeparation.noMusic
            ? 'This recording has no music to play under the dub'
            : (musicSeparation.blockedReason ?? null);
  return { musicInert, separationPanel };
}

export function HeaderPicker<T extends string>({
  label,
  ariaLabel,
  testId,
  value,
  options,
  onChange,
  menuZIndex,
  inertReason = null,
  menuContent,
  triggerValue,
  tooltipId = TOOLTIP_ID,
  triggerMark,
  menuRole = 'listbox',
}: {
  label: string;
  ariaLabel: string;
  testId: string;
  value: T;
  options: PickerOption<T>[];
  onChange: (next: T) => void;
  menuZIndex: number;
  /** When set the picker does not open; the reason shows on hover. */
  inertReason?: string | null;
  /**
   * Replaces the option rows. For a picker whose choices don't apply yet and
   * whose menu is the way to make them apply (PLAT-11714: Music, before the
   * story's audio has been separated). A function is handed the way to close
   * the menu, for content with its own buttons (PLAT-11899: Dub Level).
   */
  menuContent?: ReactNode | ((close: () => void) => ReactNode);
  /** Replaces the value on the CLOSED trigger — a state, not a choice. */
  triggerValue?: ReactNode;
  /**
   * The tooltip the inert reason shows in. The review portals its own; a
   * host that mounts these pickers elsewhere (PLAT-11877, `PlayerAudioPickers`)
   * names its own so two on one page never share an id.
   */
  tooltipId?: string;
  /** Drawn after the value, before the chevron — a flag on the state. */
  triggerMark?: ReactNode;
  /** What the open menu is to assistive tech: a list of rows, or a small dialog. */
  menuRole?: 'listbox' | 'dialog';
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  // A list scrolls past 200px; a dialog (PLAT-11927: the two-slider Levels
  // panel) is sized to its content, so a cap would clip its footer.
  const { menuRef, style } = useAnchoredDropdown(
    open,
    triggerRef,
    close,
    menuRole === 'dialog' ? undefined : 200,
  );
  const current = options.find(o => o.id === value) ?? options[0];

  return (
    <>
      <InertablePickerTrigger
        ref={triggerRef}
        type="button"
        aria-haspopup={menuRole}
        aria-expanded={open}
        aria-label={ariaLabel}
        // Inert, not disabled: a disabled button takes no pointer events, so
        // the reason could never show (the PLAT-11603 rule).
        aria-disabled={inertReason ? true : undefined}
        data-testid={testId}
        data-tooltip-id={inertReason ? tooltipId : undefined}
        data-tooltip-content={inertReason ?? undefined}
        $inert={!!inertReason}
        onClick={() => {
          if (inertReason) return;
          setOpen(o => !o);
        }}
      >
        <PickerKey>{label}</PickerKey>
        <span>{triggerValue ?? current.label}</span>
        {triggerMark}
        <ChevronDownIcon
          width="12"
          height="12"
          strokeColor={inertReason ? 'currentColor' : 'var(--accent)'}
        />
      </InertablePickerTrigger>
      {open &&
        style &&
        createPortal(
          <PickerMenu
            ref={menuRef}
            style={{
              ...style,
              zIndex: menuZIndex,
              width: 'max-content',
              minWidth: style.width,
            }}
            role={
              menuContent
                ? menuRole === 'dialog'
                  ? 'dialog'
                  : undefined
                : 'listbox'
            }
            aria-label={`${ariaLabel} options`}
          >
            {(typeof menuContent === 'function'
              ? menuContent(close)
              : menuContent) ??
              options.map((option, index) => {
                const isSelected = option.selected ?? value === option.id;
                return (
                  <React.Fragment key={option.id}>
                    {option.group &&
                      options[index - 1]?.group !== option.group && (
                        <PickerGroupHeading>{option.group}</PickerGroupHeading>
                      )}
                    <PickerRow
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      $selected={isSelected}
                      data-testid={`${testId}-${option.id}`}
                      onClick={() => {
                        onChange(option.id);
                        close();
                      }}
                    >
                      <PickerRowName>
                        <PickerRowCheck aria-hidden="true">
                          {isSelected && (
                            <CheckIcon
                              width="8"
                              height="8"
                              fillColor="currentColor"
                            />
                          )}
                        </PickerRowCheck>
                        <span style={option.labelStyle}>{option.label}</span>
                      </PickerRowName>
                      <PickerRowMeta>{option.hint}</PickerRowMeta>
                    </PickerRow>
                  </React.Fragment>
                );
              })}
          </PickerMenu>,
          document.body,
        )}
    </>
  );
}

/**
 * PLAT-11714 — what the Music picker opens onto before the story's audio has
 * been separated: the action that makes the bed, or the run already making it.
 * The same two states the language menu's "Music and Sounds" row shows, in the
 * review's own menu chrome — one action and its explanation, not the whole row
 * (re-separate, downloads and the splitter picker act on the story and stay
 * where they were).
 *
 * The run outlives this panel: separation takes minutes, the menu closes on the
 * next click, and the outcome reaches the reviewer as the host's toast. So the
 * estimate is anchored to the controller's `startedAt` rather than to this
 * component mounting (PLAT-11563), and reopening the picker rejoins the run in
 * progress instead of restarting the bar at 0%.
 */
const MusicSeparationPanel: React.FC<{
  separation: ReviewMusicSeparation;
}> = ({ separation }) => {
  const progress = useEstimatedProgress(
    separation.separating,
    estimateStemSeparationMs({ videoDurationSec: separation.durationSec }),
    { startedAt: separation.startedAt },
  );

  if (separation.separating) {
    return (
      <SeparationPanel role="status" aria-live="polite">
        <span className="heading">
          <span className="pulse" aria-hidden="true" />
          Separating Audio
          <span className="pct">{Math.round(progress)}%</span>
        </span>
        <ProgressBar value={progress} max={100} height={4} />
        <p className="note">
          This takes a few minutes. Keep reviewing — we’ll let you know when
          it’s ready.
        </p>
        <button
          type="button"
          className="quiet"
          data-testid="review-music-separate-cancel"
          onClick={separation.onCancel}
        >
          Stop Waiting
        </button>
      </SeparationPanel>
    );
  }

  return (
    <SeparationPanel>
      <p className="note">
        Split the original audio into voice and music, so the music can keep
        playing under the dubbed voice.
      </p>
      <button
        type="button"
        className="action"
        data-testid="review-music-separate"
        onClick={separation.onSeparate}
      >
        Separate Audio
      </button>
    </SeparationPanel>
  );
};

/**
 * Do two sets of levels differ in a way the reviewer can hear? The original's
 * level only counts on Both, where it is played.
 */
function levelsDiffer(
  a: DubLevels,
  b: DubLevels,
  includeOriginal: boolean,
): boolean {
  return (
    a.voiceVolume !== b.voiceVolume ||
    (includeOriginal && a.originalVolume !== b.originalVolume)
  );
}

/**
 * PLAT-11899 — what the Dub Level picker opens onto; on Both (PLAT-11927) it
 * is the Levels panel, with the original's slider above the dub's. The sliders
 * are only ever heard; the clip changes when, and only when, Apply to Clip is
 * pressed, which saves every level shown as one undo step. Every way out says
 * what it does: Apply to Clip saves and closes, Cancel and the close button
 * put the review back on the clip's levels and close. A review with no clip to
 * save to (the whole-story entry) has no footer — only the close button, and a
 * line saying the levels are not kept.
 *
 * "Apply to this line only" points the same sliders and buttons at the dubbed
 * line under the playhead instead: its levels, Apply to Line, and a status
 * about the line. The line is held while the box is ticked.
 *
 * On a clip on Both the panel is the Mix, and carries the original lead-in
 * too: heard as it is set, kept by Apply to Clip with the levels, put back by
 * Cancel. It is the clip's, so it is not shown while the box is ticked.
 */
const DubLevelPanel: React.FC<{
  levels: DubLevels;
  showOriginal: boolean;
  onChange: (levels: DubLevels) => void;
  onApply?: (levels: Partial<DubLevels>, leadIn?: DubLeadIn | null) => void;
  appliedLevels?: DubLevels;
  line?: ReviewLevelsLine;
  leadIn?: {
    value: DubLeadIn | undefined;
    applied: DubLeadIn | undefined;
    onChange: (next: DubLeadIn | undefined) => void;
  };
  close: () => void;
}> = ({ showOriginal, line, leadIn, close, ...clip }) => {
  // A line the clip has saved levels for opens on them; a tick or untick is
  // kept for that line only, so the next line opens on its own state again.
  const [choice, setChoice] = useState<{
    segmentId: string;
    lineOnly: boolean;
  } | null>(null);
  // The opening state is taken once per line, during render: read live, a
  // line slid back to the clip's level would untick the box mid-drag.
  if (line && choice?.segmentId !== line.segmentId) {
    setChoice({ segmentId: line.segmentId, lineOnly: line.hasSavedLevels });
  }
  const lineOnly =
    line && choice?.segmentId === line.segmentId
      ? choice.lineOnly
      : !!line?.hasSavedLevels;
  const scopedToLine = lineOnly && !!line;

  // Held whenever the box is ticked, by the person or by the line opening on
  // its own levels; a panel closed with it ticked lets the line go.
  const pin = line?.pin;
  useEffect(() => {
    if (!pin) return;
    pin(scopedToLine);
    return () => pin(false);
  }, [pin, scopedToLine]);

  // Ticking takes the clip's unapplied change onto the line alone: levels
  // moved and then ticked were meant for the line, and left on the clip they
  // would play on every other line too.
  const toggleLineOnly = () => {
    if (!line) return;
    // Off Both, only the Dub: the Original is neither shown nor heard.
    if (
      !lineOnly &&
      clip.onApply &&
      clip.appliedLevels &&
      levelsDiffer(clip.levels, clip.appliedLevels, showOriginal)
    ) {
      line.takeClipLevels();
      clip.onChange(
        showOriginal
          ? clip.appliedLevels
          : { ...clip.levels, voiceVolume: clip.appliedLevels.voiceVolume },
      );
    }
    setChoice({ segmentId: line.segmentId, lineOnly: !lineOnly });
  };

  const { levels, onChange } = scopedToLine ? line : clip;
  const canApply = scopedToLine || (!!clip.onApply && !!clip.appliedLevels);
  const leadInUnapplied =
    !!leadIn && !sameDubLeadIn(leadIn.value, leadIn.applied);
  const unapplied = scopedToLine
    ? line.unapplied
    : (!!clip.onApply &&
        !!clip.appliedLevels &&
        levelsDiffer(clip.levels, clip.appliedLevels, showOriginal)) ||
      leadInUnapplied;

  // Every level, the hidden original's included, the line's too: Cancel puts
  // the review back on what the clip is saved with, not just what is on screen.
  // Closed any other way than Apply or Cancel — a click outside, Escape — the
  // lines' unapplied levels are let go, as Cancel lets them go: left heard,
  // they would reopen the panel on a line nobody chose. The clip's own
  // unapplied levels stay heard, flagged on the trigger.
  const settledRef = useRef(false);
  const revertLinesRef = useRef(line?.revert);
  revertLinesRef.current = line?.revert;
  useEffect(
    () => () => {
      if (!settledRef.current) revertLinesRef.current?.();
    },
    [],
  );

  const cancel = () => {
    settledRef.current = true;
    if (
      clip.onApply &&
      clip.appliedLevels &&
      levelsDiffer(clip.levels, clip.appliedLevels, true)
    ) {
      clip.onChange(clip.appliedLevels);
    }
    if (leadInUnapplied) leadIn.onChange(leadIn.applied);
    // Restored as saved, not re-derived from the clip's levels: those are
    // still the unapplied ones until the clip's reset above renders.
    line?.revert();
    close();
  };

  return (
    <LevelPanel $wide={showOriginal}>
      <div className="header">
        <span className="title" id="review-dub-level-title">
          {showOriginal ? 'Mix' : 'Dub Level'}
        </span>
        <button
          type="button"
          className="close"
          aria-label="Close"
          data-testid="review-dub-level-close"
          onClick={cancel}
        >
          <CloseIcon width={12} height={12} />
        </button>
      </div>
      <div className="body">
        {
          // The lead-in's own level: the clip's, so not offered on a line.
          leadIn?.value && !scopedToLine && (
            <div className="sliderRow">
              <span className="rowName">
                Original
                <span className="rowSub">lead-in</span>
              </span>
              <DubLevelSlider
                value={leadInOriginalLevel(leadIn.value)}
                onChange={level =>
                  leadIn.value &&
                  leadIn.onChange({ ...leadIn.value, leadInLevel: level })
                }
                ariaLabel="Original level in the lead-in"
                width={160}
              />
              <span className="value">
                {formatDubLevel(leadInOriginalLevel(leadIn.value))}
              </span>
            </div>
          )
        }
        {showOriginal && (
          <div className="sliderRow">
            <span className="rowName">
              Original
              {leadIn?.value && <span className="rowSub">under dub</span>}
            </span>
            <DubLevelSlider
              value={levels.originalVolume}
              onChange={level => onChange({ ...levels, originalVolume: level })}
              ariaLabel="Original level"
              width={160}
            />
            <span className="value">
              {formatDubLevel(levels.originalVolume)}
            </span>
          </div>
        )}
        <div className="sliderRow">
          {showOriginal && <span className="rowName">Dub</span>}
          <DubLevelSlider
            value={levels.voiceVolume}
            onChange={level => onChange({ ...levels, voiceVolume: level })}
            ariaLabel="Dub level"
            width={showOriginal ? 160 : 176}
          />
          <span className="value">{formatDubLevel(levels.voiceVolume)}</span>
        </div>
        {leadIn && !scopedToLine && (
          <ReviewLeadInPanel
            leadIn={leadIn.value}
            onChange={leadIn.onChange}
            underDubLevel={levels.originalVolume}
            tooltipId={TOOLTIP_ID}
          />
        )}
        {line && (
          <div className="lineOption" data-testid="review-dub-level-line">
            <Checkbox
              label="Apply to this line only"
              checked={lineOnly}
              color={lineOnly ? 'var(--accent)' : 'transparent'}
              borderColor={lineOnly ? 'var(--accent)' : 'var(--border)'}
              labelColor="var(--foreground)"
              onClick={toggleLineOnly}
            />
            {!lineOnly && line.hasOwnLevels && (
              <span
                className="lineLabel"
                data-testid="review-dub-level-line-own"
              >
                This line has its own levels.
              </span>
            )}
          </div>
        )}
        {/* Only where there is nothing to apply to: on a clip the gold dot on
            the trigger and the Apply button say whether the levels are kept. */}
        {!canApply && (
          <p
            className="note"
            role="status"
            data-testid="review-dub-level-status"
          >
            Only heard in this review.
          </p>
        )}
      </div>
      {canApply && (
        <div className="footer">
          <button
            type="button"
            className="secondary"
            data-testid="review-dub-level-cancel"
            onClick={cancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary"
            data-testid="review-dub-level-apply"
            // Inert, not disabled, so it keeps its place in the tab order (the
            // PLAT-11603 rule); there is nothing to apply at the saved level.
            aria-disabled={!unapplied || undefined}
            onClick={() => {
              if (!unapplied) return;
              settledRef.current = true;
              if (scopedToLine) {
                line.onApply(levels);
              } else {
                // Only what the panel shows: off Both, the original's level
                // is neither heard nor shown, so it is not the reviewer's to
                // save.
                const shown = showOriginal
                  ? levels
                  : { voiceVolume: levels.voiceVolume };
                if (leadIn) clip.onApply?.(shown, leadIn.value ?? null);
                else clip.onApply?.(shown);
              }
              close();
            }}
          >
            {scopedToLine ? 'Apply to Line' : 'Apply to Clip'}
          </button>
        </div>
      )}
    </LevelPanel>
  );
};

// The gold "not applied" flag on the closed trigger: the review is playing a
// level the clip is not saved at.
const UnappliedDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--accent);
  flex-shrink: 0;
`;

const LevelPanel = styled.div<{ $wide: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ $wide }) => ($wide ? 296 : 272)}px;
  font-size: var(--text-xs);

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 8px 0 12px;
  }
  .title {
    font-weight: var(--font-weight-semibold);
    color: var(--foreground);
  }
  .close {
    display: inline-flex;
    color: var(--text-muted);
    background: transparent;
    border: none;
    padding: var(--spacing-sm);
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .close:hover {
    color: var(--foreground);
    background-color: var(--surface-elevated);
  }
  .body {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 12px 12px;
  }
  .sliderRow {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .rowName {
    display: flex;
    flex-direction: column;
    width: 48px;
    flex-shrink: 0;
    font-weight: var(--font-weight-semibold);
    color: var(--text-secondary);
  }
  .rowSub {
    font-size: 11px;
    font-weight: var(--font-weight-medium);
    color: var(--text-muted);
    white-space: nowrap;
  }
  .value {
    flex: 1;
    text-align: right;
    font-weight: var(--font-weight-medium);
    color: var(--foreground);
    font-variant-numeric: tabular-nums;
  }
  .lineOption {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding-top: 4px;
  }
  .lineLabel {
    margin-left: 22px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    color: var(--text-muted);
  }
  .note {
    white-space: normal;
    margin: 0;
    color: var(--text-muted);
    line-height: 1.5;
  }
  /* Cancel first, confirm right-most (design-system.md, dialog actions). */
  .footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 10px 12px;
    border-top: 1px solid var(--border);
  }
  .footer button {
    height: 28px;
    padding: 0 12px;
    border-radius: 6px;
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semibold);
    cursor: pointer;
    transition: background-color 120ms ease;
  }
  .secondary {
    border: 1px solid var(--border);
    background: transparent;
    color: var(--foreground);
  }
  .secondary:hover {
    background: var(--surface-elevated);
  }
  .primary {
    border: 1px solid var(--primary);
    background: var(--primary);
    color: var(--primary-foreground);
  }
  .primary:hover {
    background: var(--primary-hover);
    border-color: var(--primary-hover);
  }
  .primary[aria-disabled='true'] {
    border-color: var(--border);
    background: var(--surface-elevated);
    color: var(--text-muted);
    cursor: default;
  }
`;

// Mirrors the language menu's busy affordance (publishStatusUi's gold dot
// fading 1 → 0.3 → 1 over 1.4s), so "Separating Audio" reads the same wherever
// it is started from.
const pulseDot = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
`;

// A panel, not rows: nothing here is a choice between values, so the menu's
// row chrome would misdescribe it. Sized to the menu's own min-width so the
// picker doesn't jump between this and the None / From Original rows.
const SeparationPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  width: 232px;
  padding: 8px 10px;
  font-size: var(--text-xs);

  .heading {
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-xs, 4px);
    font-weight: var(--font-weight-medium);
    /* Gold (--accent) busy affordance, matching "Publishing". */
    color: var(--accent);
  }
  .pulse {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    animation: ${pulseDot} 1.4s ease-in-out infinite;
  }
  .pct {
    color: var(--text-muted);
    font-variant-numeric: tabular-nums;
  }
  .note {
    /* The menu rows set nowrap; this is prose and has to wrap. */
    white-space: normal;
    margin: 0;
    color: var(--text-muted);
    line-height: 1.5;
  }
  .action {
    /* Outline chrome, as the language menu's own Separate button has: a
       secondary control, not this surface's one green primary action. */
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 4px 10px;
    background: transparent;
    color: var(--foreground);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    transition:
      background-color 120ms ease,
      color 120ms ease;
  }
  .action:hover {
    background: var(--surface-elevated);
  }
  .quiet {
    border: none;
    border-radius: 4px;
    padding: 2px 4px;
    margin-left: -4px;
    background: transparent;
    color: var(--text-secondary);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-medium);
    cursor: pointer;
    transition:
      background-color 120ms ease,
      color 120ms ease;
  }
  .quiet:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

// The shared trigger, plus the inert state (design system: `aria-disabled`,
// muted, no hover, default cursor — the tooltip carries the reason).
const InertablePickerTrigger = styled(PickerTrigger)<{ $inert: boolean }>`
  ${p =>
    p.$inert &&
    `
    color: var(--text-muted);
    opacity: 0.6;
    cursor: not-allowed;
    &:hover { background: transparent; color: var(--text-muted); }
  `}
`;

// The pane header's own type — the Segment picker inherits it from its
// header; the player header carries the body's, so it is set here so the two
// families are one: --text-xs, medium, secondary, with the value in the
// foreground.
// A row's name, with a fixed slot for the tick so every label starts on the
// same edge whether or not it is the selected one.
const PickerRowName = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

// PLAT-11702 follow-up — the selected row is ticked, not merely coloured. The
// caption rows are drawn in their own template's face, so "styled differently"
// already means something else there and colour alone could not say which one
// is showing.
const PickerRowCheck = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 8px;
  flex-shrink: 0;
`;

// The language menu's "Switch To" heading, at the picker's scale: a smaller
// inset than the rows, so they read as nested under it (PLAT-11877).
const PickerGroupHeading = styled.div`
  padding: 8px 6px 2px;
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  color: var(--text-secondary);
`;

// Sentence case, quiet: a fact about what is on screen, not a control.
const PreviewTag = styled.span`
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-style: italic;
  line-height: 1.5;
  cursor: help;
  // A pill with a border, sitting in a row of controls, reads as a button —
  // and this is a statement about what is on screen, not something to press.
  // A dotted underline is the conventional "explanation on hover" affordance.
  text-decoration: underline dotted;
  text-underline-offset: 3px;
`;

export const Pickers = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
`;

// The setting's name, one step quieter than its value, so two pickers side by
// side read "Voice Dubbed · Music None" rather than two bare values.
const PickerKey = styled.span`
  color: var(--text-muted);
  margin-right: 2px;
`;
